import { useEffect, useMemo, useState } from "react";
import { Trans } from "react-i18next";
import { NotificationManager } from "react-notifications";
import { useBaseModal, ZatcaClient } from "services";
import { MainErrors } from "components/common/ErrorDetailsComponent";
import { Alert, Button, Col, Input, Row } from "reactstrap";
import PartialContactForm from "./PartialContactForm";
import { useTranslation } from "react-i18next";
import { useXeroClient } from "hooks";
import { fetchZatcaApps } from "pages/Xero/AccountsReceivable/constants";
import { FormGroup } from "components/common";
import Select from "react-select";

export const ChangingMessageComponent = ({ docType }) => {
  const changingMessages = [
    `Fetching ${docType} Data...`,
    "Creating XML...",
    "Adding Zatca Formatting...",
    "Sending To Zatca...",
    "Capturing Response...",
    "Confirming with Zatca...",
  ];
  const [cIndex, setCIndex] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setCIndex((prevIndex) => (prevIndex + 1) % changingMessages.length);
    }, 5000)

    return () => clearInterval(interval);
  }, []);

  return (
    <span><Trans>{changingMessages[cIndex]}</Trans></span>
  );
};

export const requiredDownloadFields = (item, taxAddress) => ({
  ContactName: item?.Contact?.Name,
  ContactCity: taxAddress?.City,
  ContactStreet: taxAddress?.AddressLine1,
  ContactCountryCode: taxAddress?.Country,
  ContactBuilding: taxAddress?.AddressLine4,
  ContactPostalCode: taxAddress?.PostalCode,
  ContactState: taxAddress?.Region,
  ContactPlot: taxAddress?.AddressLine3,
  ContactVatOrContactIdNumber: item?.Contact?.TaxNumber || item?.Contact?.id_number || '',
});

export const isValidContactDetails = (contact_details) => {
  const Address = contact_details.Addresses?.find(address => address.AddressType === 'POBOX') || {};

  const { Country } = Address || {};

  // Common fields required in all countries (street, city)
  if (!Address || !Address.AddressLine1 || !Address.City || !Country) {
      return false
  }

  const isSaudi = Country === 'SA';
  if (isSaudi) {
      // Saudi specific fields
      const plot_number = Address.AddressLine3;
      const building_number = Address.AddressLine4;
      const postal_code = Address.PostalCode;
      const tax_number = contact_details.TaxNumber;

      if (!building_number || !plot_number || !postal_code || !Address.Region) {
          return false
      }

      const v_building_number = (v) => (v && /^\d{4}$/.test(v));
      const v_postal_code = (v) => (v && /^\d{5}$/.test(v));
      const v_tax_number = (v) => (!!contact_details.id_type || (v && /^[3]\d{13}[3]$/.test(v)));

      if (![v_building_number(building_number), v_postal_code(postal_code), v_building_number(plot_number), v_tax_number(tax_number)].every(Boolean)) {
          return false
      }

      if (!(contact_details.id_type && contact_details.id_number) && !contact_details.TaxNumber) {
          return false
      }

  }
  return true
}

export const DownloadError = ({ setInvoice, item, toggle, history, missFields, callbackFunc }) => {

  const { t } = useTranslation()
  const xero = useXeroClient();

  return (
    <>
      <Alert color="warning"><Trans>You cannot proceed with this Tax Invoice, Please fill in the missing required fields</Trans>:</Alert>
      <PartialContactForm setInvoice={setInvoice} missFields={missFields} toggle={toggle} invoice={item} item={item?.Contact} t={t} xero={xero} callbackFunc={callbackFunc} />
    </>
  );
};


export const SelectedItemsDisplay = ({ invoiceSDKZatca, setSDKZatcaData, docType, formattedType, setZatcaModal, t }) => {

  const [isSending, setIsSending] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [responses, setResponses] = useState([]);
  const [expandedInvoice, setExpandedInvoice] = useState(null);
  const [zatcaApps, setZatcaApps] = useState(null);
  const [selectedZatcaApp, setSelectedZatcaApp] = useState(null);
  const [completedCount, setCompletedCount] = useState();
  const [, toggle, MainModal] = useBaseModal()
  const [targetInvoice, setTargetInvoice] = useState({});
  const client = useXeroClient();
  const [contacts, setContacts] = useState([])
  const [refresh, setRefresh] = useState(false)
  const invalid_contacts = useMemo(() => contacts.some(contact => contact.missingFields?.length > 0), [contacts])
  const missing_cr_reasons = useMemo(() => invoiceSDKZatca.some(invoice => invoice.docType === 'creditnote' && !invoice.AllocationInvoice), [invoiceSDKZatca])
  const missing_doc_types = useMemo(() => invoiceSDKZatca.some(invoice => invoice.IsSimplified == null), [invoiceSDKZatca])
  const zatca = ZatcaClient()

  const uniqueContactIds = [...new Set(invoiceSDKZatca.map(invoice => invoice.Contact.ContactID))];

  useEffect(async () => {
    setContacts((contacts) => {
      return contacts.map((c) => ({ ...c, missingFields: checkContact(c) }));
    })
  }, [refresh])

  useEffect(() => {
    setSDKZatcaData((invoices) => invoices.map((invoice) => {
      const contact_info = contacts.find(c => c.ContactID === invoice.ContactID)
      let document_type = (contact_info?.TaxNumber || contact_info?.id_number || contact_info?.CompanyNumber) ? "standard" : null;
      return {
        ...invoice,
        DocumentType: invoice.DocumentType || document_type,
        IsSimplified: invoice.IsSimplified !== null ? invoice.IsSimplified : (document_type ? document_type === 'standard' ? false : true : null),
      }
    }
    ))
  }, [contacts])

  useEffect(async () => {
    const _apps = await fetchZatcaApps(zatca)
    setZatcaApps(_apps)
    await fetchAndSetContacts()
  }, [])


  const checkContact = (contact) => {
    const taxAddress = contact?.Addresses?.find(address => address.AddressType === 'POBOX');
    let fieldsToCheck = requiredDownloadFields({ Contact: contact }, taxAddress)

    if (invoiceSDKZatca.some((invoice) => invoice.ContactID === contact.ContactID && (invoice.IsSimplified === false || invoice.DocumentType === 'standard'))) {
      const missingFields = Object.entries(fieldsToCheck)
        .filter(([fieldName, fieldValue]) => fieldValue === undefined || fieldValue === null || fieldValue === "")
        .map(([fieldName]) => fieldName);
      return missingFields
    }
    return []
  }

  const loadContact = async (id) => {
    const contact = await client.get(`contacts/${id}`).get("data");
    return { ...contact, missingFields: checkContact(contact) }
  };

  const fetchAndSetContacts = async () => {
    try {
      const contactPromises = uniqueContactIds.map(id => loadContact(id));
      const checked_contacts = await Promise.all(contactPromises);
      setContacts(checked_contacts);
    } catch (error) {
      console.error("Error fetching contacts:", error);
    }
  };

  const callbackUpdate = async (invoice) => {
    const _contact = invoice.Contact
    setContacts(contacts => contacts.map((i) => i.ContactID === _contact.ContactID ? _contact : i))
    setRefresh(() => !refresh)
  }

  const update_doctype = (e, i) => {
    setSDKZatcaData((invoices) => invoices.map((invoice) => invoice.InvoiceID === i.InvoiceID ? { ...invoice, IsSimplified: e.target.value === 'true' } : invoice))
    setRefresh(() => !refresh)
  }

  const update_all_docTypes = (e) => {
    setSDKZatcaData((invoices) => invoices.map((invoice) => !invoice.DocumentTypeConfirmed ? { ...invoice, IsSimplified: e.target.value === 'true' } : invoice))
    setRefresh(() => !refresh)
  }

  const updateBillingRef_ = (invoiceID, newBillingRef) => {
    setSDKZatcaData(invoices => invoices.map((invoice) =>
      invoice.InvoiceID === invoiceID ? { ...invoice, AllocationInvoice: newBillingRef } : invoice))
  }

  const handleSDKPost = async (values, passedValue) => {

    if (docType === "creditnote" && !values.every(value => value.hasOwnProperty('AllocationInvoice'))) {
      NotificationManager.error(t("Please enter the Billing References for all items"));
      return;
    }
    setProcessing(true);
    setIsSending(true)

    for (let i = 0; i < values.length; i++) {
      const value = values[i];
      let res_element = {}
      try {
        value.reporting = passedValue === "11";

        setCompletedCount(t(`Processing ${docType} ${i + 1} out of ${values.length}.`));

        const response = await zatca.post("sdk_send/invoice_set", value, { params: { id: selectedZatcaApp } });
        res_element = { InvoiceID: value.InvoiceID, Response: response.data }
      } catch (error) {

        let errorMessage = "Error occurred while saving";
        if (error.response && error.response.data) {
          errorMessage = error.response.data.message || "Error occurred while saving";
        }
        res_element = { InvoiceID: value.InvoiceID, Response: error.response.data }
      }
      setResponses((responses) => ([res_element, ...responses]))

      await new Promise((resolve) => setTimeout(resolve, 1000));
    }

    setProcessing(false);
    setIsSending(false)
  };

  const onClickExpand = (InvoiceID) => ({
    style: { cursor: "pointer" },
    onClick: () => {
      if (expandedInvoice === InvoiceID) {
        setExpandedInvoice(null);
      } else {
        setExpandedInvoice(InvoiceID);
      }
    },
  });

  return (

    <div>
      {!processing ?
        <div className="alert alert-info" role="alert"><span>Selected {invoiceSDKZatca?.length} {docType}s to send as {formattedType}</span></div>
        :
        <div className="alert alert-info" role="alert"><span>Sending {invoiceSDKZatca?.length} {formattedType} to Zatca.</span></div>
      }
      {zatcaApps && zatcaApps.length > 1 &&
        <Alert color="warning">
          <Trans>You have multiple zatca OnBoardings, Please select one</Trans>
          <Row>
            <Col className="col-12">
              <FormGroup label={t("Select a ZATCA application") + " *"} >
                <Select placeholder={`${t("Select a ZATCA application")}`} options={zatcaApps} value={selectedZatcaApp} onChange={(e) => { setSelectedZatcaApp(e?.value || "") }} Isclearable={false} />
              </FormGroup>
            </Col>
          </Row>
        </Alert>
      }


      {invalid_contacts &&
        <div className="alert alert-warning" role="alert">
          <span><Trans>Contacts with missing details</Trans></span>
          {contacts.filter(c => c.missingFields?.length > 0).map((contact) => (
            <div className="my-1 d-flex justify-content-between">
              <span><Trans>Missing Contact Details for </Trans><strong>{contact.Name}</strong></span>
              <Button size="sm" color="primary" onClick={() => { setTargetInvoice({ Contact: contact }); toggle(); }}><Trans>Update</Trans></Button>
            </div>
          ))}
        </div>}

      {/* <span>number of invoices selected ({invoiceSDKZatca?.length}):</span> */}

      <div className="table-responsive">
        <table className="table-container table table-sm border">
          <tbody>
            {invoiceSDKZatca?.map((i, index) => {
              const response = responses.find((r) => r.InvoiceID === i.InvoiceID);
              const status = response ? (
                response?.Response?.validationResults?.errorMessages?.length > 0 ? 'failure' :
                  response?.Response?.validationResults?.warningMessages?.length > 0 ? 'warning' :
                    response?.Response?.validationResults?.infoMessages?.length > 0 ? 'success' :
                      response?.Response?.errors || response?.Response?.errors?.length > 0 ? 'failure' :
                        'default'
              ) : 'default';

              let newBillingRef = "";

              const handleBillingRefChange = (e) => {
                newBillingRef = e.target.value;
              };

              return (
                <>
                  <tr key={i.InvoiceID || i.CreditNoteID}>
                    <td>
                      <div {...(response ? onClickExpand(i.InvoiceID) : {})} className="d-flex justify-content-between">
                        <span>{response && <i className={`fa fa-chevron-${expandedInvoice === i.InvoiceID ? "up" : "down"} mr-1`} />}{i.InvoiceNumber}</span>

                        {docType === 'creditnote' && (
                          <>
                            {i.AllocationInvoice ?
                              <><Trans>Billing Reference:</Trans> <strong>{i?.AllocationInvoice}</strong></>
                              :
                              <>
                                <div className="d-flex align-items-center">
                                  <span className="text-nowrap"><Trans>Enter Billing Reference:</Trans></span>
                                  <Input className="ml-2" size={"sm"} type="text" placeholder="INV-0000" onChange={handleBillingRefChange} />
                                  <Button size="sm" color="primary" onClick={() => updateBillingRef_(i.InvoiceID, newBillingRef)}>Save</Button>
                                </div>
                              </>
                            }
                          </>
                        )}

                        <div className="d-flex align-items-center">
                          <span className="text-nowrap mx-1"><Trans>Document Type:</Trans></span>
                          {!i.DocumentTypeConfirmed ?
                            <FormGroup>
                              <Input
                                type="select"
                                className="py-0"
                                size={"sm"}
                                onChange={(e) => update_doctype(e, i)}
                                value={i.IsSimplified}
                              >
                                {i.IsSimplified === null && <option value={null}>-------------</option>}
                                <option value={true}>{t('Simplified Invoice')}</option>
                                <option value={false}>{t('Tax Invoice')}</option>
                              </Input>
                            </FormGroup> :
                            <span className="font-weight-bold">({i.DocumentType === 'standard' ? t('Tax Invoice') : t('Simplified Invoice')})</span>
                          }
                        </div>

                        {status === 'success' && (
                          <span className="text-success"><Trans>Reported</Trans> <i className="fa fa-check-circle" /></span>
                        )}
                        {status === 'failure' && (
                          <span className="text-danger"><Trans>Rejected</Trans> <i className="fa fa-times-circle" /></span>
                        )}
                        {status === 'warning' && (
                          <span className="text-warning"><Trans>Reported with Warnings</Trans> <i className="fa fa-exclamation-triangle" /></span>
                        )}
                        {status === 'default' && (
                          <span className="text-muted"><Trans>Ready</Trans> <i className="fa fa-plus-circle" /></span>
                        )}
                        {response?.Response?.error && (
                          <span className="text-danger"><Trans>Invalid Data</Trans> <i className="fa fa-times-circle" /></span>
                        )}
                      </div>
                    </td>
                  </tr>
                  {expandedInvoice === i.InvoiceID && response && (
                    <tr><td><MainErrors justMessages details={response.Response} showWarnings={true} typeOfInv={formattedType} /></td></tr>
                  )}
                  {expandedInvoice === i.InvoiceID && response.Response.error && (
                    <tr><td><MainErrors justMessages details={{ message: response.Response.error }} showWarnings={true} typeOfInv={formattedType} /></td></tr>
                  )}
                </>
              )
            })}
          </tbody>
        </table>
      </div>

      {invoiceSDKZatca.find(i => !i.DocumentTypeConfirmed) && 
        <div className="row justify-content-end mb-1 pb-2">
          <div className="col-4 d-flex justify-content-between ">
            <span><Trans>Mark all as </Trans></span>
            <FormGroup>
              <Input
                type="select"
                className="py-0"
                size={"sm"}
                onChange={(e) => update_all_docTypes(e)}
              >
                <option value={true}>{t('Simplified Invoice')}</option>
                <option value={false}>{t('Tax Invoice')}</option>
              </Input>
            </FormGroup>
          </div>
        </div>
      }

      <div className="text-center">
        {!processing ? (
          <p className="border p-2 mb-4" style={{ fontSize: "1.2em" }}>
            {responses.length > 0 ?
              // <Trans>Do you want to send these {docType}s again to Zatca?</Trans>
              <Trans>Bulk {docType} task completed. Send again or click on each {docType} for it's response.</Trans>
              :
              <Trans>Are you sure you want to send these {docType}s to Zatca?</Trans>
            }
          </p>
        ) : (
          <div style={{ fontSize: "1.2em" }}>
            <span><i className="fa fa-spinner fa-spin" style={{ color: "#72B566" }} /> {completedCount}</span>
            <p className="text-muted"><ChangingMessageComponent docType={docType} /></p>
          </div>
        )}

        <div className="d-flex justify-content-between">
          <Button disabled={isSending} onClick={() => setZatcaModal(false)} color="danger"><Trans>Cancel</Trans></Button>
          {!isSending ?
            <div>
              {/* <Button color="primary" disabled={missing_doc_types || missing_cr_reasons || invalid_contacts || isSending || (zatcaApps && zatcaApps.length > 1 && !selectedZatcaApp)} onClick={() => { handleSDKPost(invoiceSDKZatca, "00") }}><Trans>Send to Compliance</Trans></Button> */}
              <Button disabled={missing_doc_types || missing_cr_reasons || invalid_contacts || isSending || (zatcaApps && zatcaApps.length > 1 && !selectedZatcaApp)} onClick={() => { handleSDKPost(invoiceSDKZatca, "11") }}><Trans>Send to Reporting</Trans></Button>
            </div>
            :
            <Button disabled><span className="fa fa-spinner fa-spin" /> <Trans>Sending To Zatca</Trans></Button>}
        </div>
      </div>
      <MainModal title={t('Set Invoice details')}>
        <DownloadError setInvoice={callbackUpdate}
          item={{ Contact: (({ missingFields, ..._contact }) => _contact)(targetInvoice?.Contact || {}) }} toggle={toggle} missFields={targetInvoice?.Contact?.missingFields} callbackFunc={() => { }} />
      </MainModal>
    </div>
  )
};
